/* eslint-disable no-shadow */
import Vue, { VueConstructor } from 'vue';
import VueRouter, { Route, RouterOptions } from 'vue-router';

import appVersion from './plugins/appVersion';
import indexedDb from './plugins/indexedDB';
import auth from './plugins/auth';
import withoutWatchers from './plugins/withoutWatchers';
import axios from './plugins/axios';
import config from './plugins/config';
import cookie from './plugins/cookie';
import router from './plugins/router';
import store from './plugins/store';
import pwa from './plugins/pwa';
import formatters from './plugins/formatters';
import eventbus from './plugins/eventbus';
import logging from './plugins/logging';
import validation from './plugins/validation';
import media from './plugins/media';
import localization from './plugins/localization';
import systemInfo from './plugins/systemInfo';
import servicelayer from './plugins/servicelayer';
import services from './services';
import storage from './plugins/storage';
import configLoaderGuard from './guards/configLoaderGuard';
import translationGuard from './guards/translationGuard';
import unverifiedUserGuard from './guards/unverifiedUserGuard';
import roleGuard from './guards/roleGuard';
import guard from './plugins/guards';
import dialog from './plugins/dialog';
import errorHandling from './plugins/errorHandling';
import _ from './utils';
import components from './components';
import directives from './directives';
import App from './app.vue';

export interface CoreOptions {
  appId?: string;
  hasServiceWorker?: boolean;
  router?: RouterOptions | InstanceType<typeof VueRouter> | undefined;
  /**
   * Static translations are loaded via this option,
   * **however** if the specifies remote translations these will be loaded and supersede the static ones.
   * It is recommended to use either external translations or static ones, not both.
   */
  translations?: any;
  debug?: boolean;
  Vue?: VueConstructor;
  /** Core does not mount the app itself it only loads the framework parts */
  noMount?: boolean;
}

declare module 'vue/types/vue' {
  interface Vue {
    $appIsEmbedded: boolean,
    $appConfigPath: string,
    $appSource: string,
    $appId: string
  }
  // eslint-disable-next-line no-shadow
  interface VueConstructor {
    $appIsEmbedded: boolean,
    $appConfigPath: string,
    $appSource: string,
    $appId: string
  }
}

declare module 'vue/types/options' {
  interface ComponentOptions<V extends Vue> {
    /**
     * Which layout wrapper component should be loaded.
     * Use either the name of the component as a string or the component directly.
     * @example ```layout: 'layout-mobile'```
     * @example ```layout: LayoutMobile // import and use```
     * @example ```layout: ()=>import('../layouts/layout-mobile.vue')```
     * @example ```layout: ['layout-mobile', 'layout-desktop'] // whichever exists```
     * @example ```layout: {'layout-mobile': isMobile, 'layout-desktop': !isMobile} // conditional```
     */
    layout?: any;
  }
}

export default {
  loadLayouts() {
    let atLeastOneLayoutLoaded = false;
    try {
      const success = this.installLayouts(require.context('@src/views/layouts', true));
      atLeastOneLayoutLoaded = success || atLeastOneLayoutLoaded;
    } catch (error: any) {
      // Do nothing
    }
    try {
      const success = this.installLayouts(require.context('@/src/views/layouts', true));
      atLeastOneLayoutLoaded = success || atLeastOneLayoutLoaded;
    } catch (error: any) {
      // Do nothing
    }
    try {
      const success = this.installLayouts(require.context('./views/layouts', true));
      atLeastOneLayoutLoaded = success || atLeastOneLayoutLoaded;
    } catch (error: any) {
      // Do nothing
    }
    try {
      const success = this.installLayouts(require.context('@/node_modules/@glittr/frontend-core/src/views/layouts', true));
      atLeastOneLayoutLoaded = success || atLeastOneLayoutLoaded;
    } catch (error: any) {
      // Do nothing
    }

    if (!atLeastOneLayoutLoaded && process.env!.VUE_APP_BUILD_CONFIGURATION !== 'test') {
      Vue.$log.error('No layouts were loaded, this can be due to a misconfiguration of webpack or your paths for the layouts is setup wrong');
    }
  },
  installLayouts(reqLayouts: __WebpackModuleApi.RequireContext) {
    let didInstallLayout = false;
    try {
      // const reqLayouts = require.context(path, true);
      reqLayouts.keys().forEach((componentPath) => {
        let componentName = componentPath.substr(componentPath.lastIndexOf('/') + 1, componentPath.length - 1);
        componentName = componentName.replace('.vue', '');
        const component = reqLayouts(componentPath).default;
        Vue.component(componentName, component);
        didInstallLayout = true;
      });
    } catch (error: any) {
      // Do nothing
    }
    return didInstallLayout;
  },
  mountApp(Vue: VueConstructor, options: CoreOptions) {
    const app = new Vue({
      router: Vue.$router,
      render: (h) => h(App),
    });

    if (Vue.$version.buildConfiguration === 'test') {
      app.$mount();
    } else {
      const mountDiv = document.createElement('div');
      const appNode = document.getElementById(Vue.$appId);
      if (appNode === null || appNode === undefined) {
        const errorMessage = `Unable to mount app, no node with id '${Vue.$appId}' was found`;
        Vue.$log.error(errorMessage);
        throw new Error(errorMessage);
      } else {
        const appEl = appNode.insertBefore(mountDiv, null);
        appEl.style.height = '100%';
        appEl.style.width = '100%';
        app.$mount(appEl);
      }
    }
  },
  /** Loads HTML attributes for widget usage */
  loadHTMLAttributeConfigs(dataAttributes: Record<string, string | number | boolean | undefined>) {
    // Initial read
    Object.keys(dataAttributes).forEach((key) => {
      const attributeValue = dataAttributes[key];
      (Vue.$config.values as any)[key] = attributeValue;
    });
    Vue.$guards.waitForAppLoad().then(() => {
      // HTML attributes have priority so overwrite any loaded config values
      Object.keys(dataAttributes).forEach((key) => {
        const attributeValue = dataAttributes[key];
        (Vue.$config.values as any)[key] = attributeValue;
      });
      if (dataAttributes.language) {
        Vue.$translation.set(dataAttributes.language as string);
      }
      if (dataAttributes.route) {
        Vue.$router.replace(dataAttributes.route as string);
      }
    });
  },
  install(Vue: VueConstructor, options: CoreOptions) {
    // Logging is used everywhere and is essential, load it first
    Vue.use(logging, options);

    Vue.$appIsEmbedded = process.env.VUE_APP_IS_EMBEDDED === 'true';
    Vue.prototype.$appIsEmbedded = Vue.$appIsEmbedded;

    const dataAttributes = _.getAllDataAttributes(document.currentScript!);
    const APP_ID = dataAttributes['mount-id']?.toString() ?? options?.appId ?? 'app';
    Vue.$appId = APP_ID;
    Vue.prototype.$appId = Vue.$appId;

    if (options.router && dataAttributes['router-mode']) {
      options.router.mode = dataAttributes['router-mode'] as any;
    }

    // Best to place this as close to the entry point of the app as possible
    // Otherwise it might end up in a chunk which is not loaded from the page
    // and the attributes would be unaccessible (currentScript has to be the one loaded on the page)
    const scriptSourceRaw = (document?.currentScript?.attributes as any)?.src?.value;
    const parser = document.createElement('a');
    parser.href = scriptSourceRaw;
    const scriptSource = `${parser.origin + parser.pathname.substring(0, parser.pathname.lastIndexOf('/'))}/`;
    Vue.$appSource = scriptSource ?? process.env.VUE_APP_URL ?? '/';
    Vue.prototype.$appSource = Vue.$appSource;

    let configPath = dataAttributes['config-path']?.toString() ?? Vue.prototype.$appSource;
    // Might not be the best thing to remove 'js' from path, but there will be only one usecase for this
    configPath = configPath.replace(/\/$/g, '').replace(/\/js$/g, '');
    Vue.$appConfigPath = configPath;
    Vue.prototype.$appConfigPath = Vue.$appConfigPath;

    // Inject plugins
    Vue.use(appVersion, options);
    Vue.use(errorHandling);
    Vue.use(axios, { debug: false, ...options });
    Vue.use(systemInfo, options);
    Vue.use(withoutWatchers, options);
    Vue.use(eventbus, options);
    Vue.use(indexedDb, options);
    Vue.use(formatters, options);
    Vue.use(dialog, options);
    Vue.use(router, options);
    Vue.use(pwa, options);
    Vue.use(directives, options);
    Vue.use(components, options);
    Vue.use(guard, options);
    Vue.use(cookie, options);
    Vue.use(storage, options);
    Vue.use(config, options);
    Vue.use(store, options);
    Vue.use(validation, options);
    Vue.use(localization, options);
    Vue.use(media, options);

    // These will block loading of pages until the config & translations are loaded
    Vue.$guards.push(configLoaderGuard);
    Vue.$guards.push(translationGuard);

    Vue.$guards.waitForAppLoad()
      .then(() => {
        Vue.use(servicelayer, options);
        Vue.use(services);
        Vue.use(auth, options);
        // This will check if the user is unverified and redirect them to the verify-user page
        Vue.$guards.push(unverifiedUserGuard);
        // This will check if the user has the correct role
        Vue.$guards.push(roleGuard);
      });

    this.loadHTMLAttributeConfigs(dataAttributes);

    if (!options.noMount) {
      this.mountApp(Vue, options);
    }

    this.loadLayouts();
  },
};
